import React from 'react';
import { Badge, Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import {
  disponivelParaCeder,
  motivoDestinoInvalido,
  recebidoPorOrdemDe,
  somaCedida,
} from '../regrasDoPassoSocios';

/** O que a leitura do upload trouxe de cada sócio do vigente, por CPF/CNPJ. */
export interface IVigenteDoSocio {
  quotas?: number | null;
  nome?: string | null;
  tipo_vinculo?: string | null;
}

const ROTULO_ASPECTO: Record<string, string> = {
  quotas: 'quotas',
  endereco: 'endereço',
  nome: 'nome',
  profissao: 'profissão',
  vinculo: 'administração',
  estado_civil: 'estado civil',
  representante: 'representante',
};

const fmt = (n: number) => n.toLocaleString('pt-BR');
const pct = (n: number, total: number) =>
  total > 0 ? `${((n / total) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%` : '—';
const ehAdmin = (v?: string | null) => v === 'socio_administrador' || v === 'administrador';
const docDe = (s: any) => String(s?.cpf || s?.cnpj || '').replace(/\D/g, '');

/** "49.990 → 39.990" com o novo em destaque; entrante "— → 10.000"; sem mudança, só o número. */
const CelulaDeQuotas: React.FC<{ antes: number | null; depois: number; semQuotas: boolean }> = ({
  antes,
  depois,
  semQuotas,
}) => {
  if (semQuotas) return <>—</>;
  if (antes == null) {
    return (
      <Text as="span" fontWeight="bold">
        — → {fmt(depois)}
      </Text>
    );
  }
  if (antes === depois) return <>{fmt(depois)}</>;
  return (
    <Text as="span">
      <Text as="span" color="gray.500">
        {fmt(antes)}
      </Text>
      {' → '}
      <Text as="span" fontWeight="bold" color={depois > antes ? 'green.600' : 'red.600'}>
        {fmt(depois)}
      </Text>
    </Text>
  );
};

/** "Sim"/"Não", com "Não → Sim" quando o ato muda e "corrigido da leitura" sem ato. */
const CelulaAdministra: React.FC<{
  antes: boolean | null;
  depois: boolean;
  corrigido: boolean;
}> = ({ antes, depois, corrigido }) => {
  const simNao = (v: boolean) => (v ? 'Sim' : 'Não');
  if (corrigido) {
    return (
      <>
        <Text as="span" fontSize="sm">
          {simNao(depois)}
        </Text>
        <Text fontSize="xs" color="orange.600">
          corrigido da leitura
        </Text>
      </>
    );
  }
  if (antes == null || antes === depois) {
    return (
      <Text as="span" fontSize="sm">
        {simNao(depois)}
      </Text>
    );
  }
  return (
    <Text as="span" fontSize="sm" fontWeight="bold" color="blue.700">
      {simNao(antes)} → {simNao(depois)}
    </Text>
  );
};

/** Uma linha de "Cessões declaradas". */
interface ICessaoDaLista {
  key: string;
  texto: string;
  /** Por que a cessão não vale (destino que saiu do quadro ou sem destino), ou null. */
  motivo: string | null;
  /** A linha perdeu o destino (ele foi removido do quadro): falta escolher quem recebe. */
  semDestino: boolean;
}

interface Props {
  socios: any[];
  vigentePorDoc: Record<string, IVigenteDoSocio>;
  /** Total de quotas que o quadro depois do ato precisa fechar (capital novo ou o vigente). */
  totalAlvo: number;
  /** Total do quadro vigente inteiro, inclusive de quem a leitura trouxe sem CPF/CNPJ. */
  totalVigente?: number;
}

/**
 * Resumo do quadro no topo do passo Sócios (25/09): uma linha por pessoa, com o antes e o depois
 * lado a lado. A leitura que o operador fazia card a card (e que a Revisão mostrava como "MANTÉM"
 * mesmo com quotas mudando) fica numa tela só.
 */
export const QuadroAntesDepois: React.FC<Props> = ({
  socios,
  vigentePorDoc,
  totalAlvo,
  totalVigente,
}) => {
  const recebido = recebidoPorOrdemDe(socios);
  const linhas = socios.map((s, ordem) => {
    const vig = vigentePorDoc[docDe(s)];
    const entra = s?.acao === 'adicionar';
    const sai = s?.acao === 'sair';
    const quotasNoVigente = vig?.quotas ?? null;
    // Administrador sem quotas: "—" só para quem não tinha quotas no vigente. Quem tinha mostra
    // "antes → 0": as quotas dele precisam ir para alguém (item 8, 25/09).
    const administradorSemQuotas = s?.tipo_vinculo === 'administrador' && !Number(s?.quotas);
    const adminNaoSocio = administradorSemQuotas && !quotasNoVigente;
    let antes: number | null = null;
    if (!entra) {
      if (quotasNoVigente != null) antes = quotasNoVigente;
      else if (!adminNaoSocio) antes = Number(s?.quotas) || 0;
    }
    const depois = sai || administradorSemQuotas ? 0 : Number(s?.quotas) || 0;
    // Quem cede mais do que pode fica com a conta negativa, mesmo com o campo parado em 0 (item 2,
    // 25/09). A conta do schema (28/09): quem sai cede o vigente; quem altera, o vigente e o que
    // recebe no mesmo ato.
    let saldo: number | null = null;
    if (quotasNoVigente != null && (sai || s?.acao === 'alterar')) {
      saldo = disponivelParaCeder(s?.acao, quotasNoVigente, recebido[ordem] || 0) - somaCedida(s);
    }
    const negativo = depois < 0 || (saldo != null && saldo < 0);
    const administraAntes = entra ? null : ehAdmin(vig?.tipo_vinculo ?? s?.tipo_vinculo);
    const administraDepois = sai ? !!s?.permanece_como_administrador : ehAdmin(s?.tipo_vinculo);
    const aspectos = ((s?.tipos_alteracao_socio ?? []) as string[]).map(
      a => ROTULO_ASPECTO[a] ?? a,
    );
    // Administra mudou sem o aspecto "Vínculo" (e sem sair): correção do vínculo lido, não ato.
    const vinculoDeclarado =
      s?.acao === 'alterar' && ((s?.tipos_alteracao_socio ?? []) as string[]).includes('vinculo');
    const administraCorrigido =
      !entra &&
      !sai &&
      !vinculoDeclarado &&
      administraAntes != null &&
      administraAntes !== administraDepois;
    const quotasMudam = antes != null && antes !== depois && !sai;
    let situacao: { texto: string; cor: string };
    if (entra) situacao = { texto: 'Entra', cor: 'green' };
    else if (sai) situacao = { texto: 'Sai', cor: 'red' };
    else if (s?.acao === 'alterar' || quotasMudam) situacao = { texto: 'Altera', cor: 'blue' };
    else situacao = { texto: 'Mantém', cor: 'gray' };
    return {
      ordem,
      nome: s?.nome || vig?.nome || `Sócio ${ordem + 1}`,
      pj: s?.tipo_pessoa === 'pj',
      administrador: s?.tipo_vinculo === 'administrador',
      adminNaoSocio,
      antes,
      depois,
      negativo,
      administraAntes,
      administraDepois,
      administraCorrigido,
      aspectos,
      situacao,
    };
  });

  // Quadro vigente inteiro (item 6, 25/09). Sem o total, soma só quem a leitura trouxe com
  // CPF/CNPJ.
  const somaDoVigentePorDoc = Object.values(vigentePorDoc).reduce(
    (a, v) => a + (Number(v?.quotas) || 0),
    0,
  );
  const totalAntes = totalVigente ?? somaDoVigentePorDoc;
  const totalDepois = linhas.reduce((a, l) => a + l.depois, 0);
  const diferenca = totalDepois - totalAlvo;

  const nomeDaOrdem = (ordem: number) => socios[ordem]?.nome || `Sócio ${ordem + 1}`;
  // Key pelo índice do cedente e da linha: duas cessões iguais não repetem a key (item 7, 25/09).
  // Cessão a quem saiu do quadro depois fica marcada como inválida (item 5); a que perdeu o
  // destino (removido do quadro), também (28/09).
  const cessoes: ICessaoDaLista[] = [];
  socios.forEach((s, ordemCedente) => {
    if (s?.acao !== 'sair' && s?.acao !== 'alterar') return;
    (s?.redistribuicao_quotas ?? []).forEach((d: any, linha: number) => {
      if (!d || !(Number(d.qtd) > 0)) return;
      const quem = s?.nome || '?';
      const qtd = fmt(Number(d.qtd));
      const key = `${ordemCedente}-${linha}`;
      if (d.socio_ordem == null) {
        cessoes.push({
          key,
          texto: `${quem} cede ${qtd} quotas`,
          motivo: 'falta escolher quem recebe',
          semDestino: true,
        });
        return;
      }
      const motivo = motivoDestinoInvalido(socios, d.socio_ordem);
      cessoes.push({
        key,
        texto: `${quem} cede ${qtd} quotas a ${nomeDaOrdem(d.socio_ordem)}`,
        motivo: motivo && `inválida: ${motivo}`,
        semDestino: false,
      });
    });
  });

  // Só fecha com todo "depois" >= 0, as cessões válidas e o total batendo.
  const nomesNegativos = linhas.filter(l => l.negativo).map(l => l.nome);
  const temCessaoInvalida = cessoes.some(c => c.motivo != null);
  const fecha = diferenca === 0 && nomesNegativos.length === 0 && !temCessaoInvalida;
  let textoDoTotal = `${fmt(totalDepois)} de ${fmt(totalAlvo)} quotas ✓`;
  if (diferenca > 0) textoDoTotal = `sobram ${fmt(diferenca)} quotas`;
  if (diferenca < 0) textoDoTotal = `faltam ${fmt(-diferenca)} quotas`;
  if (cessoes.some(c => c.semDestino)) textoDoTotal = 'cessão sem destinatário';
  if (cessoes.some(c => c.motivo != null && !c.semDestino)) {
    textoDoTotal = 'cessão a quem sai do quadro';
  }
  if (nomesNegativos.length > 0) textoDoTotal = `quotas negativas em ${nomesNegativos.join(', ')}`;

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3} mb={4} bg="white">
      <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
        <Text fontWeight="bold" fontSize="sm">
          Quadro: antes → depois do ato
        </Text>
        <Badge colorScheme={fecha ? 'green' : 'red'} fontSize="xs">
          {textoDoTotal}
        </Badge>
      </Flex>
      <Box overflowX="auto">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>Sócio</Th>
              <Th>Situação</Th>
              <Th isNumeric>Quotas</Th>
              <Th isNumeric>% depois</Th>
              <Th>Administra</Th>
            </Tr>
          </Thead>
          <Tbody>
            {linhas.map(l => (
              <Tr key={l.ordem} opacity={l.situacao.texto === 'Sai' ? 0.75 : 1}>
                <Td>
                  <Text fontWeight="semibold" fontSize="sm">
                    {l.nome}
                    {l.pj && (
                      <Badge ml={2} fontSize="2xs">
                        PJ
                      </Badge>
                    )}
                  </Text>
                  {l.administrador && (
                    <Text fontSize="xs" color="gray.500">
                      administrador, não sócio
                    </Text>
                  )}
                  {l.negativo && (
                    <Text fontSize="xs" color="red.600">
                      quotas negativas: cede mais do que tem
                    </Text>
                  )}
                </Td>
                <Td>
                  <Badge colorScheme={l.situacao.cor}>{l.situacao.texto}</Badge>
                  {l.aspectos.length > 0 && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      {l.aspectos.join(', ')}
                    </Text>
                  )}
                </Td>
                <Td isNumeric>
                  <CelulaDeQuotas antes={l.antes} depois={l.depois} semQuotas={l.adminNaoSocio} />
                </Td>
                <Td isNumeric>{l.adminNaoSocio ? '—' : pct(l.depois, totalDepois)}</Td>
                <Td>
                  <CelulaAdministra
                    antes={l.administraAntes}
                    depois={l.administraDepois}
                    corrigido={l.administraCorrigido}
                  />
                </Td>
              </Tr>
            ))}
            <Tr>
              <Td fontWeight="bold">Total</Td>
              <Td />
              <Td isNumeric fontWeight="bold">
                {totalAntes && totalAntes !== totalDepois
                  ? `${fmt(totalAntes)} → ${fmt(totalDepois)}`
                  : fmt(totalDepois)}
              </Td>
              <Td isNumeric>{totalDepois > 0 ? '100%' : '—'}</Td>
              <Td />
            </Tr>
          </Tbody>
        </Table>
      </Box>
      {cessoes.length > 0 && (
        <Box mt={2}>
          <Text fontSize="xs" fontWeight="semibold" color="gray.700">
            Cessões declaradas
          </Text>
          {cessoes.map(c => (
            <Text key={c.key} fontSize="xs" color={c.motivo ? 'red.600' : 'gray.700'}>
              • {c.texto}
              {c.motivo && ` — ${c.motivo}`}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default QuadroAntesDepois;
